import { useState } from 'react';
import {
  MeetingNoteStatus,
  useCalendarEventMeetingNote,
} from 'src/meetings/hooks/useCalendarEventMeetingNote';
import { useGenerateCalendarEventMeetingNote } from 'src/meetings/hooks/useGenerateCalendarEventMeetingNote';
import { useSetMeetingNoteReviewed } from 'src/meetings/hooks/useSetMeetingNoteReviewed';
import { MeetingNoteTab } from 'src/meetings/detail/tabs/MeetingNoteTab';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import type { CalendarEvent } from 'src/meetings/models';
import { ReviewableMeetingNote } from './ReviewableMeetingNote';

type Props = {
  event: CalendarEvent;
};

export function FamilyMeetingNoteTab({ event }: Props) {
  const [summarise, setSummarise] = useState(true);

  const { meetingNote, refetch } = useCalendarEventMeetingNote(event.id, {
    enabled: event.hasTranscription,
  });
  const { generateMeetingNote, isLoading: isRegenerating } = useGenerateCalendarEventMeetingNote(
    event.id,
  );
  const { setMeetingNoteReviewed, isLoading: isSettingReviewed } = useSetMeetingNoteReviewed(
    event.id,
  );

  const templateId = meetingNote?.templateId;
  const meetingNoteId = meetingNote?.id;
  const clientId = meetingNote?.clientId;

  const handleRegenerate = async () => {
    if (!templateId) return;
    try {
      await generateMeetingNote({ templateId, summarise });
      refetch();
    } catch (error) {
      notifyAndLogError('Failed to regenerate meeting note', 'Please try again later', error, {
        eventId: event.id,
        templateId,
      });
    }
  };

  const handleToggleReviewed = async (isReviewed: boolean) => {
    if (!meetingNoteId || !clientId) return;
    try {
      await setMeetingNoteReviewed({ clientId, meetingNoteId, isReviewed });
    } catch (error) {
      notifyAndLogError('Failed to update review status', 'Please try again later', error, {
        eventId: event.id,
        meetingNoteId,
      });
    }
  };

  if (meetingNote?.status === MeetingNoteStatus.READY && meetingNote.markdownContent) {
    return (
      <ReviewableMeetingNote
        title={event.title}
        markdownContent={meetingNote.markdownContent}
        onRegenerate={templateId ? handleRegenerate : undefined}
        isRegenerating={isRegenerating}
        summarise={summarise}
        onSummariseChange={setSummarise}
        isReviewed={meetingNote.isReviewed ?? false}
        onToggleReviewed={meetingNoteId && clientId ? handleToggleReviewed : undefined}
        isSettingReviewed={isSettingReviewed}
      />
    );
  }

  return <MeetingNoteTab event={event} />;
}
